import React, { createContext, useContext, useEffect, useState } from "react";
const { isPlatform } = require("@ionic/react");
import config from "../config";

const { CDN_ROOT } = config;
const ringer = new Audio(require("../sounds/not.mp3"));
const isMobile = isPlatform("android") || isPlatform("ios");

interface NotificationData {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  payload?: any;
  onClick?: () => void;
  sound?: boolean;
}

interface NotificationContextType {
  permission: NotificationPermission;
  notificationsEnabled: boolean;
  setNotificationsEnabled: (enabled: boolean) => void;
  requestPermission: () => Promise<NotificationPermission>;
  notify: (data: NotificationData) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [permission, setPermission] = useState<NotificationPermission>(
    "Notification" in window ? Notification.permission : "denied"
  );

  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(
    () => {
      return localStorage.getItem("notificationsEnabled") !== "false"; // default to true
    }
  );

  const requestPermission = async (): Promise<NotificationPermission> => {
    if (isMobile) {
      console.warn("Notifications are not supported on mobile browsers.");
      setNotificationsEnabled(false);
      return "denied";
    }
    setNotificationsEnabled(true);
    if (!("Notification" in window)) {
      console.warn("This browser does not support desktop notifications.");
      setNotificationsEnabled(false);
      return permission;
    }

    if (permission === "default") {
      setNotificationsEnabled(false);
      const result = await Notification.requestPermission();
      if (result === "granted") {
        setNotificationsEnabled(true);
      } else {
        setNotificationsEnabled(false);
      }
      setPermission(result);
      return result;
    }

    if (Notification.permission === "denied") {
      alert(
        "Notifications are blocked in your browser. Please enable them from your browser's site settings."
      );
      setTimeout(() => {
        setNotificationsEnabled(false);
      }, 100);
    }

    return permission;
  };

  const notify = (data: NotificationData) => {
    if (permission !== "granted" || !notificationsEnabled) {
      console.warn(
        "Notification skipped due to disabled state or lack of permission."
      );
      return;
    }

    const notification = new Notification(data.title, {
      body: data.body,
      icon: `${CDN_ROOT}/statics/logo/letters-square.png`,
      badge: `${CDN_ROOT}/statics/logo/letters-square.png`,
    });

    if (data.sound) {
      ringer.play().catch(() => {});
    }

    notification.onclick = () => {
      data.onClick?.();
      notification.close();
    };
  };

  useEffect(() => {
    localStorage.setItem("notificationsEnabled", String(notificationsEnabled));
  }, [notificationsEnabled]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === "notificationsEnabled") {
        setNotificationsEnabled(event.newValue !== "false");
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        permission,
        notificationsEnabled,
        setNotificationsEnabled,
        requestPermission,
        notify,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useDesktopNotification must be used within a DesktopNotificationProvider"
    );
  }
  return context;
};
